import { useState } from 'react';
import { PICKER_LIMIT } from '@constants/limits';
import { DEBOUNCE_MS } from '@constants/timing';
import { locationTypeLabel } from '@locales/id/labels';
import { LOCATION_PICKER } from '@locales/id/locations';
import { locationPaths, useApi, type DestinationPage, type LocationListItem, type LocationPage, type LocationType } from '@lib/api';
import { useDebounced } from '@lib/hooks';
import { Combobox, type ComboOption } from '@ui';

/** A chosen location: its id and the name to show. */
export interface PickedLocation { id: number; name: string }

/** What a result row offers, from either source (the scoped list, or the nationwide destinations). */
export type PickerRow = Pick<LocationListItem, 'id' | 'code' | 'name' | 'type' | 'regencyName'> & { provinceName?: string }

/** `none`: the value for "no location" (null). */
type Choice = number | 'none';

interface Props {
  id?: string;
  /** for a picker without a `Field` around it (a filter row) */
  label?: string;
  describedBy?: string;
  invalid?: boolean;
  disabled?: boolean;
  value: PickedLocation | null;
  onChange: (l: PickedLocation | null) => void;
  /** offer "no location" first, with this text (e.g. "Belum ditempatkan") */
  noneLabel?: string;
  /** never offered (e.g. a delivery note's own origin) */
  exclude?: number;
  /** the line under each result; type, regency/city and code by default */
  describe?: (l: PickerRow) => string;
  placeholder?: string;
  /** only locations in this province (a province admin choosing an operator's location, a province filter) */
  provinceCode?: string;
  /** only locations of this type (a location's parent: a hub's SPBG) */
  type?: LocationType;
  /** only locations of any of these types (a Surat Jalan's allowed destinations); `type` wins when both are set */
  types?: readonly LocationType[];
  /**
   * `destinations`: where a Surat Jalan may go, nationwide (GET /locations/destinations, needs shipment.operate);
   * by default the locations the account may see (its scope).
   */
  source?: 'scope' | 'destinations';
}

const DESCRIBE = (l: PickerRow) => `${locationTypeLabel(l.type)} · ${l.regencyName} · ${l.code}`;

/**
 * Choose one location by searching its name or code (GET /locations?q=, PICKER_LIMIT results): the network can have
 * thousands, so they are never all listed. Searches only while the list is open.
 */
export function LocationPicker({ id, label, describedBy, invalid, disabled, value, onChange, noneLabel, exclude, describe = DESCRIBE, placeholder = LOCATION_PICKER.placeholder, provinceCode, type, types, source = 'scope' }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), DEBOUNCE_MS.urlSearch);
  const filter = { q: query, limit: PICKER_LIMIT, provinceCode, type: type ?? types?.join(',') };
  const found = useApi<LocationPage | DestinationPage>(open ? (source === 'destinations' ? locationPaths.destinations(filter) : locationPaths.list(filter)) : null, { keepPrevious: true });
  const rows: PickerRow[] = (found.data?.items ?? []).filter((l) => l.id !== exclude);
  const options: ComboOption<Choice>[] = [
    ...(noneLabel && !query ? [{ value: 'none' as const, title: noneLabel }] : []),
    ...rows.map((l) => ({ value: l.id, title: l.name, sub: describe(l) })),
  ];
  const more = (found.data?.meta.total ?? 0) > rows.length;
  return (
    <Combobox<Choice>
      id={id}
      label={label}
      describedBy={describedBy}
      invalid={invalid}
      disabled={disabled}
      placeholder={placeholder}
      selected={value?.name ?? noneLabel ?? ''}
      query={q}
      onQuery={setQ}
      options={options}
      onPick={(o) => onChange(o.value === 'none' ? null : { id: o.value, name: o.title })}
      loading={found.loading}
      error={found.error}
      onRetry={found.retry}
      empty={query ? LOCATION_PICKER.noMatch(query) : LOCATION_PICKER.none}
      footer={more ? LOCATION_PICKER.narrow : undefined}
      onOpen={(o) => {
        setOpen(o);
        if (o) setQ('');
      }}
    />
  );
}
